import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, ShieldCheck, Ruler, Droplets } from "lucide-react";

const products = [
    { name: "Fiat Ducato / Peugeot Boxer Rubber Cab Mat", detail: "2007 onwards", price: "£79.99" },
    { name: "Ford Transit Rubber Cab Mat", detail: "2015 onwards", price: "from £79.99" },
    { name: "Mercedes Sprinter Cab Mat — Heavy-Duty Rubber", detail: "2019 onwards", price: "£79.99" },
    { name: "Ford Transit Custom Rubber Cab Mat", detail: "2024 onwards", price: "£69.99" },
    { name: "Rubber Cab Step Mats", detail: "Fiat Ducato, Peugeot Boxer & Ford Transit", price: "£23.99" },
    { name: "Rubber Step Mats for VW T5/T6 Side Doors", detail: "T5 & T6", price: "from £23.99" },
];

const features = [
    { icon: Ruler, text: "Precision fit for every vehicle — no trimming, no guesswork" },
    { icon: ShieldCheck, text: "Tough, hard-wearing rubber built for muddy boots and touring life" },
    { icon: Droplets, text: "Easy-clean, non-slip materials that simply wipe or hose down" },
    { icon: MapPin, text: "Designed and made right here in the UK" },
];

const reveal = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function Maxmats() {
    return (
        <div className="min-h-screen bg-indigo-950" data-testid="maxmats-page">
            <header className="border-b border-indigo-400/25 bg-indigo-950/90 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
                    <img src="/assets/logo.png" alt="Planet Valet logo" className="h-10 w-auto" />
                    <Link to="/" className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-blue-200 hover:text-white transition-colors" data-testid="maxmats-back-link">
                        <ArrowLeft className="w-4 h-4" /> Back to Planet Valet
                    </Link>
                </div>
            </header>

            <section className="relative py-24 sm:py-32 overflow-hidden pv-noise" data-testid="maxmats-hero">
                <div className="absolute inset-0 pv-stars opacity-50 pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
                    <div className="lg:col-span-7">
                        <motion.p {...reveal} className="text-xs tracking-[0.3em] uppercase font-semibold text-blue-300 mb-6" data-testid="maxmats-eyebrow">
                            Also from Planet Valet
                        </motion.p>
                        <motion.h1 {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="font-serif-display text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-medium text-white mb-8" data-testid="maxmats-heading">
                            Maxmats.{" "}
                            <span className="italic bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">Durable, UK-made</span>{" "}
                            motorhome cab mats.
                        </motion.h1>
                        <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.2 }} className="text-base sm:text-lg leading-relaxed font-normal text-white/85 max-w-xl mb-10" data-testid="maxmats-copy">
                            The same people who keep your motorhome spotless also make high-quality,
                            custom-fit rubber cab mats for motorhomes and campers — tough, stylish and
                            precision-cut for your exact vehicle.
                        </motion.p>
                        <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.3 }} className="flex flex-wrap items-center gap-4">
                            <motion.a
                                whileTap={{ scale: 0.97 }}
                                href="https://www.maxmats.uk"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 bg-white hover:bg-blue-50 text-indigo-950 text-sm tracking-[0.12em] uppercase font-bold px-8 py-4 transition-colors duration-300"
                                data-testid="maxmats-visit-button"
                            >
                                Visit maxmats.uk <ArrowUpRight className="w-4 h-4" />
                            </motion.a>
                            <Link to="/" className="text-xs tracking-[0.2em] uppercase font-semibold text-blue-200 hover:text-white transition-colors" data-testid="maxmats-home-link">
                                Back to Planet Valet
                            </Link>
                        </motion.div>
                    </div>
                    <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.25 }} className="lg:col-span-5 flex justify-center">
                        <div className="border border-indigo-400/25 bg-white p-10 sm:p-14" data-testid="maxmats-logo-panel">
                            <img src="/assets/maxmats-logo.png" alt="Maxmats logo" className="w-48 sm:w-64 h-auto mx-auto" />
                        </div>
                    </motion.div>
                </div>
            </section>

            <section className="py-16 sm:py-24 border-t border-indigo-400/25" data-testid="maxmats-features">
                <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                    {features.map((f, i) => (
                        <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} data-testid={`maxmats-feature-${i}`}>
                            <f.icon className="w-6 h-6 text-blue-300 mb-4" />
                            <p className="text-sm sm:text-base font-normal text-white/85 leading-relaxed">{f.text}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section className="py-16 sm:py-24 border-t border-indigo-400/25" data-testid="maxmats-gallery">
                <div className="max-w-7xl mx-auto px-6 sm:px-10">
                    <motion.p {...reveal} className="text-xs tracking-[0.3em] uppercase font-semibold text-blue-300 mb-6">The mats up close</motion.p>
                    <motion.h2 {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="font-serif-display text-3xl sm:text-5xl tracking-tight font-medium text-white mb-14" data-testid="maxmats-gallery-heading">
                        Made to fit, made to last.
                    </motion.h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="maxmats-page-photos">
                        {[
                            { src: "/assets/mat-product.webp", alt: "Fiat Ducato Peugeot Boxer cab mat", caption: "Fiat Ducato / Peugeot Boxer 2007 onwards £79.99 (trim: black, grey, red, yellow, blue, white, green)" },
                            { src: "/assets/mat-fitted.webp", alt: "Cab mat fitted in a Fiat Ducato", caption: "Fitted in a Fiat Ducato cab — 2007 onwards £79.99 (trim: black, grey, red, yellow, blue, white, green)" },
                            { src: "/assets/step-red.webp", alt: "Cab step mats with red trim", caption: "Fiat Ducato / Peugeot Boxer cab step mats £23.99 — red trim (trim: black, grey, red, yellow, blue, white, green)" },
                            { src: "/assets/step-black.webp", alt: "Cab step mats with black trim", caption: "Fiat Ducato / Peugeot Boxer cab step mats £23.99 — black trim (trim: black, grey, red, yellow, blue, white, green)" },
                        ].map((p, i) => (
                            <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} data-testid={`maxmats-page-photo-${i}`}>
                                <div className="overflow-hidden border border-indigo-400/25">
                                    <img src={p.src} alt={p.alt} className="w-full aspect-[4/3] object-cover hover:scale-[1.02] transition-transform duration-700" />
                                </div>
                                <p className="mt-3 text-sm sm:text-base tracking-[0.15em] uppercase font-semibold text-blue-200">{p.caption}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 sm:py-24 border-t border-indigo-400/25" data-testid="maxmats-products">
                <div className="max-w-7xl mx-auto px-6 sm:px-10">
                    <motion.p {...reveal} className="text-xs tracking-[0.3em] uppercase font-semibold text-blue-300 mb-6">Popular fits</motion.p>
                    <motion.h2 {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="font-serif-display text-3xl sm:text-5xl tracking-tight font-medium text-white mb-14" data-testid="maxmats-products-heading">
                        A mat made for your motorhome.
                    </motion.h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-indigo-400/25 border border-indigo-400/25" data-testid="maxmats-products-grid">
                        {products.map((p, i) => (
                            <motion.a
                                key={i}
                                {...reveal}
                                transition={{ ...reveal.transition, delay: i * 0.06 }}
                                href="https://www.maxmats.uk"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group bg-indigo-900/40 hover:bg-indigo-900/70 p-8 transition-colors duration-500"
                                data-testid={`maxmats-product-${i}`}
                            >
                                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-blue-200 mb-3">{p.detail}</p>
                                <h3 className="font-serif-display text-xl tracking-tight text-white mb-6 leading-snug">{p.name}</h3>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs tracking-[0.25em] uppercase font-bold text-blue-300">{p.price}</span>
                                    <ArrowUpRight className="w-4 h-4 text-blue-300/60 group-hover:text-blue-300 transition-colors" />
                                </div>
                            </motion.a>
                        ))}
                    </div>
                    <motion.p {...reveal} className="mt-8 text-sm text-blue-200 font-normal" data-testid="maxmats-products-note">
                        Many more vehicle fits available on the Maxmats website.
                    </motion.p>
                </div>
            </section>

            <footer className="border-t border-indigo-400/25 bg-indigo-900/40 py-10" data-testid="maxmats-footer">
                <div className="max-w-7xl mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <p className="text-xs text-blue-200 font-normal">Maxmats — from the same hands as Planet Valet, with the same standard of care for your cab floor.</p>
                    <a href="https://www.maxmats.uk" target="_blank" rel="noopener noreferrer" className="text-xs tracking-[0.2em] uppercase font-bold text-blue-300 hover:text-white transition-colors" data-testid="maxmats-footer-link">
                        www.maxmats.uk
                    </a>
                </div>
            </footer>
        </div>
    );
}
